import { SPEC_FLAGS_MAP } from "../../../../Common/constants/SpecFlagConstants";

import { ESV_DETAILS } from "../../../../../utils/Constant";

export const OOS_ALERT_FLAGS = new Set(["2", "6"]);
const UPDATE_SPECS_PROMPT_FLAGS = new Set([...OOS_ALERT_FLAGS, "7"]);

export const OOS_RESULTS_REVIEW_TAB = {
  RESULTS: "results",
  REVIEW: "review",
};

export const RELEASE_RESULT_STATUS = {
  INVESTIGATION_IN_PROCESS: "Investigation in Process",
  DECISION_REQUIRED: "Decision Required",
  AWAITING_SAMPLE: "Awaiting Sample",
};

const AWAITING_SAMPLE_TOOLTIP = "A new sample has been requested. We are waiting for the sample to be received.";

const getSpecFlag = (value) => (value != null ? value.toString() : "");

const isUpdateSpecsPromptFlag = (item) => (
  Boolean(item) && UPDATE_SPECS_PROMPT_FLAGS.has(getSpecFlag(item.test_spec_flag))
);

const normalizeIsUnderReviewFlag = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value === true || value === 1 || value === "1") {
    return true;
  }
  if (value === false || value === 0 || value === "0") {
    return false;
  }
  return null;
};

export const getResultsReviewTabKey = (item) => (
  normalizeIsUnderReviewFlag(item?.is_under_review) === true
    ? OOS_RESULTS_REVIEW_TAB.REVIEW
    : OOS_RESULTS_REVIEW_TAB.RESULTS
);

export const hasResultsReviewTabKey = (item) => (
  normalizeIsUnderReviewFlag(item?.is_under_review) === true
);

const getReleaseResultStatus = (item) => (
  (item?.release_result_status ?? "").toString().trim()
);

const isAwaitingSampleStatus = (status) => (
  status.toLowerCase() === RELEASE_RESULT_STATUS.AWAITING_SAMPLE.toLowerCase()
);

const isInvestigationInProcessStatus = (status) => (
  status.toLowerCase() === RELEASE_RESULT_STATUS.INVESTIGATION_IN_PROCESS.toLowerCase()
  || isAwaitingSampleStatus(status)
);

const formatInvestigationResultTooltip = (item, resultText) => {
  const specLabel = SPEC_FLAGS_MAP[getSpecFlag(item?.test_spec_flag)]?.label;
  const resultLine = specLabel && specLabel !== "-"
    ? `${resultText} - ${specLabel}`
    : resultText;
  const requestedBy = (item?.contact_csm_requested_by ?? "").toString().trim();
  return requestedBy ? `Requested by: ${requestedBy}\n${resultLine}` : resultLine;
};

const getInvestigationFollowUp = ({
  item, statusText, resultText, messageText,
}) => {
  const awaitingSample = isAwaitingSampleStatus(statusText);
  return {
    type: "investigation",
    text: awaitingSample
      ? RELEASE_RESULT_STATUS.INVESTIGATION_IN_PROCESS
      : statusText || RELEASE_RESULT_STATUS.INVESTIGATION_IN_PROCESS,
    message: messageText,
    tooltip: awaitingSample
      ? AWAITING_SAMPLE_TOOLTIP
      : formatInvestigationResultTooltip(item, resultText),
  };
};

const isDecisionRequiredStatus = (status) => (
  status.toLowerCase() === RELEASE_RESULT_STATUS.DECISION_REQUIRED.toLowerCase()
);

const formatDecisionRequiredFollowUp = (resultText) => (
  `${resultText} - ${RELEASE_RESULT_STATUS.DECISION_REQUIRED}`
);

/**
 * Get the test name to request suggested specs for, only when the test has no existing spec
 * @param {Object} item released test item
 * @returns {String} test name, or "" when the test already has a spec
 */
export const getSuggestedSpecTest = (item) => {
  const specs = (item?.specs ?? "").toString().trim();
  if (!item?.test || (specs && specs !== "-")) {
    return "";
  }
  return item.test;
};

export const isInvestigationInProcess = (item) => (
  isInvestigationInProcessStatus(getReleaseResultStatus(item))
);

export const getOosFollowUp = (item) => {
  const statusText = getReleaseResultStatus(item);
  const resultText = (item?.result ?? "").toString().trim() || "-";
  const messageText = (item?.message ?? "").toString().trim();

  if (isInvestigationInProcessStatus(statusText)) {
    return getInvestigationFollowUp({
      item, statusText, resultText, messageText,
    });
  }

  if (isDecisionRequiredStatus(statusText)) {
    return {
      type: "decision_required",
      text: formatDecisionRequiredFollowUp(resultText),
      resultText,
      statusLabel: RELEASE_RESULT_STATUS.DECISION_REQUIRED,
      message: messageText,
      tooltip: "",
    };
  }

  return {
    type: "decision_required",
    text: statusText || resultText,
    message: messageText,
    tooltip: "",
  };
};

export const isOosReviewSelectable = (item) => (
  !isInvestigationInProcess(item)
);

export const partitionTestsForOosRelease = (tableData) => {
  const tests = Array.isArray(tableData) ? tableData : [];
  const reviewTests = tests.filter((item) => (
    getResultsReviewTabKey(item) === OOS_RESULTS_REVIEW_TAB.REVIEW
  ));
  const resultsTests = tests.filter((item) => (
    getResultsReviewTabKey(item) !== OOS_RESULTS_REVIEW_TAB.REVIEW
  ));
  return { reviewTests, resultsTests };
};

export const sampleHasResultsReviewTabs = (sampleResultsData) => {
  if (!sampleResultsData) {
    return false;
  }
  const tests = (sampleResultsData.data || []).flatMap((entry) => (
    Array.isArray(entry?.[1]) ? entry[1] : []
  ));
  return tests.some(hasResultsReviewTabKey);
};

export const isReleasedResultOutOfSpec = (item) => isUpdateSpecsPromptFlag(item);

/**
 * Retrieves the Update Specs prompt hide flag from esv_details in sessionStorage.
 * @returns {boolean} true if user chose not to show the suggestion again
 */
export const isUpdateSpecsPromptHidden = () => {
  try {
    const config = sessionStorage.getItem(ESV_DETAILS);
    if (!config) return false;
    return JSON.parse(config)?.hideUpdateSpecsPrompt === true;
  } catch (error) {
    return false;
  }
};

/**
 * Stores the Update Specs prompt hide flag in esv_details in sessionStorage.
 */
export const hideUpdateSpecsPromptPermanently = () => {
  const existingData = JSON.parse(sessionStorage.getItem(ESV_DETAILS) || "{}");
  sessionStorage.setItem(ESV_DETAILS, JSON.stringify({
    ...existingData,
    hideUpdateSpecsPrompt: true,
  }));
};

export const shouldOfferUpdateSpecsPrompt = (item) => (
  isReleasedResultOutOfSpec(item) && !isUpdateSpecsPromptHidden()
);
